import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { BearingEnvelope, Point2D } from '@paragon/contract-library';

import { LengthSoftLockState, MoveSoftLockState } from '../../library/layout-snapping/interactive-snapping';

export type EnvelopeEndpoint = 'left' | 'right';

export interface BearingEnvelopeLengthEdit {
  readonly type: 'bearingEnvelopeLength';
  readonly guid: string;
  readonly endpoint: EnvelopeEndpoint;
  readonly baseEnvelope: BearingEnvelope;
  readonly lengthSoftLockState: LengthSoftLockState;
  readonly currentPatch: {
    readonly leftPoint?: Point2D;
    readonly rightPoint?: Point2D;
  };
}

export interface BearingEnvelopeMoveEdit {
  readonly type: 'bearingEnvelopeMove';
  readonly draggedGuid: string;
  readonly editedEnvelopes: ReadonlyArray<{
    readonly guid: string;
    readonly baseEnvelope: BearingEnvelope;
  }>;
  readonly dragStartPoint: Point2D;
  readonly moveSoftLockState: MoveSoftLockState;
  readonly currentDelta: {
    readonly x: number;
    readonly y: number;
  };
}

export type InteractiveEdit = BearingEnvelopeLengthEdit | BearingEnvelopeMoveEdit | null;

export interface InteractiveEditingState {
  readonly activeEdit: InteractiveEdit;
}

const initialState: InteractiveEditingState = {
  activeEdit: null,
};

export const interactiveEditingSlice = createSlice({
  name: 'interactiveEditing',
  initialState,
  reducers: {
    __UNSAFE_startLengthEdit(
      state,
      action: PayloadAction<{
        guid: string;
        endpoint: EnvelopeEndpoint;
        baseEnvelope: BearingEnvelope;
      }>,
    ) {
      const { guid, endpoint, baseEnvelope } = action.payload;
      state.activeEdit = {
        type: 'bearingEnvelopeLength',
        guid,
        endpoint,
        baseEnvelope,
        lengthSoftLockState: null,
        currentPatch: {},
      };
    },

    __UNSAFE_startMoveEdit(
      state,
      action: PayloadAction<{
        draggedGuid: string;
        editedEnvelopes: ReadonlyArray<{
          guid: string;
          baseEnvelope: BearingEnvelope;
        }>;
        dragStartPoint: Point2D;
      }>,
    ) {
      const { draggedGuid, dragStartPoint, editedEnvelopes } = action.payload;
      state.activeEdit = {
        type: 'bearingEnvelopeMove',
        draggedGuid,
        editedEnvelopes: [...editedEnvelopes],
        dragStartPoint,
        moveSoftLockState: { x: null, y: null },
        currentDelta: { x: 0, y: 0 },
      };
    },

    // Update actions - can be called frequently during drag
    updateLengthEditPatch(
      state,
      action: PayloadAction<{
        leftPoint?: Point2D;
        rightPoint?: Point2D;
        lengthSoftLockState?: LengthSoftLockState;
      }>,
    ) {
      if (state.activeEdit?.type === 'bearingEnvelopeLength') {
        const { lengthSoftLockState, ...patch } = action.payload;
        state.activeEdit.currentPatch = patch;
        if (lengthSoftLockState !== undefined) {
          state.activeEdit.lengthSoftLockState = lengthSoftLockState;
        }
      }
    },

    updateMoveEditDelta(
      state,
      action: PayloadAction<{
        currentDelta: { x: number; y: number };
        moveSoftLockState?: MoveSoftLockState;
      }>,
    ) {
      if (state.activeEdit?.type === 'bearingEnvelopeMove') {
        const { currentDelta, moveSoftLockState } = action.payload;
        state.activeEdit.currentDelta = currentDelta;
        if (moveSoftLockState !== undefined) {
          state.activeEdit.moveSoftLockState = moveSoftLockState;
        }
      }
    },

    clearActiveEdit(state) {
      state.activeEdit = null;
    },
  },
});

export const interactiveEditing = interactiveEditingSlice.reducer;
